import { router } from '@inertiajs/vue3';
import { UserCancelledError, type VerifyResponse } from '@laravel/passkeys';
import { usePasskeyVerify } from '@laravel/passkeys/vue';
import { computed, type MaybeRefOrGetter } from 'vue';
import { route } from 'ziggy-js';

interface PasskeyVerificationOptions {
    /** Options + submit endpoints; defaults to the passkey sign-in routes. */
    routes?: { options: string; submit: string };
    remember?: MaybeRefOrGetter<boolean>;
    /** Defaults to visiting the URL the server answers with. */
    onSuccess?: (response: VerifyResponse) => void;
}

/**
 * Runs a passkey ceremony (browser prompt → server verification).
 *
 * Cancelling the browser prompt isn't an error worth showing, so `errorMessage`
 * stays empty in that case.
 */
export function usePasskeyVerification(options: PasskeyVerificationOptions = {}) {
    const { verify, isLoading, isSupported, error, errorInstance } =
        usePasskeyVerify({
            routes: options.routes,
            remember: options.remember,
            onSuccess:
                options.onSuccess ??
                ((response) => router.visit(response.redirect ?? route('home'))),
        });

    const errorMessage = computed(() =>
        errorInstance.value instanceof UserCancelledError
            ? ''
            : (error.value ?? ''),
    );

    return { verify, isLoading, isSupported, errorMessage };
}
